import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

/**
 * Pipeline Stage Tracker — driven entirely by the Recruiting Opportunity's assigned pipeline.
 * Uses Top-Recruit pipeline stages only. Universal Opportunity Status is shown separately.
 */
export function StageTracker({ stages = [], currentStageId, onMove, moving = false, enteredAt }) {
  const ordered = [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const currentIdx = ordered.findIndex((s) => s.id === currentStageId);
  const clickable = typeof onMove === "function";
  const curRef = useRef(null);
  useEffect(() => {
    const el = curRef.current;
    const box = el?.parentElement;
    if (el && box) box.scrollLeft = el.offsetLeft - box.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2;
  }, [currentStageId]);

  if (!ordered.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4" data-testid="stage-tracker-unknown">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pipeline Stage</p>
        <p className="text-sm text-slate-600 mt-1.5">Pipeline: <b>Unknown</b>. No program path has been chosen yet — use <b>Change Pipeline</b> to select one and its stage.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="stage-tracker">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Pipeline Stage{ordered[currentIdx] && <span className="normal-case tracking-normal text-sm font-semibold text-slate-900 ml-2" data-testid="stage-current-name">{ordered[currentIdx].name}</span>}
          {ordered[currentIdx] && enteredAt && <span className="normal-case tracking-normal text-xs font-normal text-slate-500 ml-1.5" data-testid="stage-entered-at">since {new Date(enteredAt.length === 10 ? `${enteredAt}T12:00:00` : enteredAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
        </p>
        {ordered[currentIdx] && (
          <span className="text-xs text-slate-500">
            Stage {currentIdx + 1} of {ordered.length}
          </span>
        )}
      </div>
      <div className="flex items-stretch gap-1 overflow-x-auto tr-scroll pb-1" role="list">
        {ordered.map((s, i) => {
          const done = currentIdx > -1 && i < currentIdx;
          const active = i === currentIdx;
          return (
            <button
              key={s.id}
              ref={active ? curRef : undefined}
              type="button"
              disabled={!clickable || active || moving}
              onClick={() => clickable && !active && onMove(s.id)}
              data-testid={`stage-step-${i}`}
              title={`${s.name}${done ? " (completed)" : active ? " (current)" : ""}`}
              aria-current={active ? "step" : undefined}
              className={cn(
                "group relative flex-1 min-w-[92px] flex flex-col items-start justify-center rounded-md border px-2.5 py-1.5 text-left transition-colors",
                active
                  ? "border-brand bg-brand text-white shadow-sm ring-2 ring-brand/20"
                  : done
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-dashed border-slate-200 bg-white text-slate-400",
                clickable && !active && "hover:border-brand/50 hover:bg-brand/10 cursor-pointer",
                (!clickable || active) && "cursor-default"
              )}
            >
              <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                {done ? <><Check className="w-3 h-3" />Done</> : active ? "Current" : `${i + 1}`}
              </span>
              <span className="text-xs font-semibold leading-snug w-full break-words">{s.name}</span>
            </button>
          );
        })}
      </div>
      {clickable && (
        <p className="mt-2 text-[11px] text-slate-400">Click a stage to move this opportunity. History is preserved and audited.</p>
      )}
    </div>
  );
}
